import { Trans, useLingui } from '@lingui/react/macro';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { TEMPLATE_RESOLUTIONS } from 'modules/manager/domain/template-resolutions';
import { useCallback, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  MediaManagerTemplateFormValues,
  getDefaultTemplateTypeOption,
} from 'modules/manager/ui/components/forms/media-manager-template-form';

interface Args {
  templateGroupId: number;
  existingNames: string[];
  templatesCount: number;
  onCreated: () => void;
  title?: string;
}

function useCreateTemplateModal({
  templateGroupId,
  existingNames,
  templatesCount,
  onCreated,
  title,
}: Args) {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(() => {
    setError(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      setIsLoading(true);
      setError(null);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await mediaManagerRepository.createTemplate({
          name: values.name.trim(),
          objects: [],
          version: '5.3.0',
          templateGroupId,
          order: templatesCount,
          width: resolution.width,
          height: resolution.height,
          templateType: values.templateType.value,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        });
        onCreated();
        close();
      } catch (err) {
        console.error('Failed to create media manager template:', err);
        setError(isTemplateNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось создать шаблон, попробуйте ещё раз`);
      } finally {
        setIsLoading(false);
      }
    },
    [templateGroupId, templatesCount, onCreated, close, t],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          {title ?? <Trans>Создать шаблон</Trans>}
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: '',
            resolution: DEFAULT_RESOLUTION_OPTION,
            templateType: getDefaultTemplateTypeOption(),
            subtype: null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useCreateTemplateModal;
